import { useCallback, useEffect, useRef, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Typography,
    IconButton,
    Button,
    Grid,
    TextField,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FilterListIcon from '@mui/icons-material/FilterList';
import { connect } from 'react-redux';
import {
    getEntitiesRequest,
    getEntityGlobalsRequest,
} from '../../../redux/actions/getEntity';
import { generatePublicUrlForFileInAWSS3Bucket } from '../../../services/uploadFileOnAWS_S3_Bucket';
import { GameCssStyles } from '../../../components/GameManagement/GameCssStyles';
import MediaSelectDialog from '../../../components/GameManagement/FantasyGame/MediaSelectDialog';

export function RapHousieEntertainmentEntityDialog(props) {
    const { open, onClose, onSubmit, formGameData, selectedItem, handleChange } = props;

    const currentYear = new Date().getFullYear();
    const yearOptions = Array.from({ length: currentYear - 1949 }, (_, i) => 1950 + i);

    const LIMIT = 100;
    const hasFetchedEntityGlobals = useRef(false);
    const scrollRef = useRef(null);

    const [fetchedPages, setFetchedPages] = useState(new Set([1]));
    const [currentPage, setCurrentPage] = useState(1);
    const [paginationData, setPaginationData] = useState({});
    const totalPages = paginationData?.totalPages || 1;

    // 🔹 Filters
    const [showFilter, setShowFilter] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [typeFilter, setTypeFilter] = useState('');
    const [yearFilter, setYearFilter] = useState('');
    const [languageFilter, setLanguageFilter] = useState('');
    const [industryFilter, setIndustryFilter] = useState('');
    const [countryFilter, setCountryFilter] = useState('');
    const [artistTypeFilter, setArtistTypeFilter] = useState('');

    const [openMediaDialog, setOpenMediaDialog] = useState(false);
    const [selectedEntertainmentEntity, setSelectedEntertainmentEntity] = useState(null);
    const [entertainment_entity_data, setEntertainmentEntity] = useState([]);
    const [entertainment_entity_filter, setEntertainmentEntityFilter] = useState(null);

    // 🔹 Load initial entities
    useEffect(() => {
        if (open) {
            setCurrentPage(1);
            setFetchedPages(new Set([1]));
            setEntertainmentEntity([]);
            props?.getEntities({ page: 1, limit: LIMIT });
        }
    }, [open]);

    // 🔹 Load globals once
    useEffect(() => {
        if (!hasFetchedEntityGlobals.current && !props?.ENTITY_GLOBALS_DATA && !props?.ENTITY_GLOBALS_ERROR) {
            hasFetchedEntityGlobals.current = true;
            props?.getEntityGlobals();
        } else {
            setEntertainmentEntityFilter(props?.ENTITY_GLOBALS_DATA);
        }
    }, [props?.ENTITY_GLOBALS_DATA, props?.ENTITY_GLOBALS_ERROR]);

    // 🔹 Append new data with unique check
    useEffect(() => {
        if (props?.ENTITIES_DATA) {
            setPaginationData(props?.ENTITIES_PAGINATION_DATA || {});
            setEntertainmentEntity((prev) => {
                const merged = [...prev, ...props.ENTITIES_DATA];
                const unique = Array.from(new Map(merged.map((item) => [item._id, item])).values());
                return unique;
            });
        }
    }, [props?.ENTITIES_DATA, props?.ENTITIES_PAGINATION_DATA]);

    // 🔹 Infinite scroll
    const handleScroll = useCallback(() => {
        const container = scrollRef.current;
        if (!container) return;

        const isBottom = container.scrollTop + container.clientHeight >= container.scrollHeight - 100;
        if (isBottom && currentPage < totalPages && !fetchedPages.has(currentPage + 1)) {
            const nextPage = currentPage + 1;
            setCurrentPage(nextPage);
            setFetchedPages((prev) => new Set(prev).add(nextPage));
            props?.getEntities({
                page: nextPage,
                limit: LIMIT,
                displayName: searchText,
                type: typeFilter,
                language: languageFilter,
                country: countryFilter,
                industry: industryFilter,
                artistTypeFilter: artistTypeFilter,
                year: yearFilter,
            });
        }
    }, [currentPage, totalPages, fetchedPages, props, searchText]);

    useEffect(() => {
        const container = scrollRef.current;
        if (container) container.addEventListener('scroll', handleScroll);
        return () => {
            if (container) container.removeEventListener('scroll', handleScroll);
        };
    }, [handleScroll]);

    // 🔹 Search
    const handleSearch = () => {
        setCurrentPage(1);
        setFetchedPages(new Set([1]));
        setEntertainmentEntity([]);
        props?.getEntities({
            page: 1,
            limit: LIMIT,
            displayName: searchText,
            type: typeFilter,
            language: languageFilter,
            country: countryFilter,
            industry: industryFilter,
            artistTypeFilter: artistTypeFilter,
            year: yearFilter,
        });
    };

    const handleClearSearch = () => {
        setSearchText('');
        setTypeFilter('');
        setLanguageFilter('');
        setIndustryFilter('');
        setCountryFilter('');
        setArtistTypeFilter('');
        setYearFilter('');
        setCurrentPage(1);
        setFetchedPages(new Set([1]));
        setEntertainmentEntity([]);
        props?.getEntities({ page: 1, limit: LIMIT });
    };

    const handleSubmit = () => {
        onSubmit?.(selectedItem);
        onClose();
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                fullScreen
                maxWidth="md"
                sx={{ m: 3 }}
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                        m: 0,
                        overflow: 'hidden',
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        px: 3,
                        py: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'stretch',
                        gap: 1,
                    }}
                >
                    <Typography fontWeight="bold" sx={{ flex: 1, fontSize: 20 }}>
                        Entertainment Entities
                    </Typography>

                    <Box
                        sx={{
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}
                    >
                        <Box sx={{ flex: 0.9, flexDirection: 'row', display: 'flex', gap: 2, mr: 2, mt: 1, mb: 0 }}>
                            <TextField
                                fullWidth
                                label="Search by name"
                                variant="outlined"
                                size="small"
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                sx={GameCssStyles.formSingleLineTextField_sx}
                            />
                            <Button variant="contained" onClick={handleSearch}>
                                Search
                            </Button>
                            <Button variant="outlined" onClick={handleClearSearch}>
                                Clear
                            </Button>
                        </Box>

                        <Box sx={{ flex: 0.1, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                            <IconButton
                                onClick={() => {
                                    if (showFilter) {
                                        setTypeFilter('');
                                    }
                                    setShowFilter(!showFilter);
                                }}
                            >
                                <FilterListIcon />
                            </IconButton>
                            <IconButton onClick={onClose}>
                                <CloseIcon />
                            </IconButton>
                        </Box>
                    </Box>
                </DialogTitle>

                <DialogContent dividers={false} sx={{ p: 0, overflow: 'hidden' }}>
                    <Box
                        sx={{
                            height: '100%',
                            backgroundColor: '#f4f1f9',
                            justifyContent: 'center',
                            display: 'flex',
                            flex: 1,
                            flexGrow: 1,
                            flexDirection: 'row',
                            gap: 0,
                            overflow: 'hidden',
                        }}
                    >
                        {/* Entity Grid */}
                        <Box
                            ref={scrollRef}
                            sx={{
                                m: 2,
                                p: 2,
                                height: '100%',
                                width: '100%',
                                overflowY: 'auto',
                            }}
                        >
                            <Grid container spacing={2} sx={{ width: '100%' }}>
                                {[...entertainment_entity_data]?.map((item) => {
                                    const isSelected = selectedItem?.entityId === item._id;
                                    const multimedia = isSelected ? selectedItem?.details?.multimediaUrl : null;

                                    return (
                                        <Grid item key={item?._id} xs={12} sm={6} md={4} lg={3}>
                                            <Box
                                                sx={{
                                                    height: 140,
                                                    width: 240,
                                                    borderRadius: 2,
                                                    overflow: 'hidden',
                                                    backgroundColor: 'whitesmoke',
                                                    backgroundSize: 'cover',
                                                    backgroundPosition: 'center',
                                                    display: 'flex',
                                                    alignItems: 'flex-end',
                                                    padding: 1,
                                                    border: isSelected ? '2px solid #1976d2' : '1px solid #ccc',
                                                    justifyContent: 'center',
                                                    alignItems: 'center',
                                                }}
                                                onClick={() => {
                                                    setSelectedEntertainmentEntity(item);
                                                    setOpenMediaDialog(true);
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        width: 80,
                                                        height: 80,
                                                        borderRadius: 2,
                                                        overflow: 'hidden',
                                                        //backgroundImage: `url(${generatePublicUrlForFileInAWSS3Bucket(multimedia)})`,
                                                        backgroundImage: `url(${generatePublicUrlForFileInAWSS3Bucket(item?.multimedia?.[0]?.url)||generatePublicUrlForFileInAWSS3Bucket(item?.multimediaV2?.[0]?.url)||generatePublicUrlForFileInAWSS3Bucket(item?.image) || generatePublicUrlForFileInAWSS3Bucket(multimedia)})`,
                                                        backgroundSize: 'cover',
                                                        backgroundPosition: 'center',
                                                        mr: 2,
                                                        flexShrink: 0,
                                                    }}
                                                />

                                                <Box sx={{ flexGrow: 1 }}>
                                                    <Typography fontWeight="bold" sx={{ fontSize: 12 }}>
                                                        {item?.displayName}
                                                    </Typography>
                                                    <Typography variant="body2" noWrap>
                                                        {item?.genres?.join(', ') || 'N/A'}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {item?.type}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Grid>
                                    );
                                })}
                            </Grid>
                        </Box>

                        {/* Filters Panel */}
                        {showFilter && (
                            <Box
                                sx={{
                                    height: '100%',
                                    width: '25%',
                                    borderLeft: '1px solid #ccc',
                                    borderTop: '1px solid #ccc',
                                    borderBottom: '1px solid #ccc',
                                    backgroundColor: '#f9f9f9',
                                    p: 2,
                                    ml: 2,
                                    display: 'flex',
                                    flexDirection: 'column',
                                }}
                            >
                                <Box sx={{ flex: 1, overflowY: 'auto' }}>
                                    <Typography variant="h6" gutterBottom>
                                        Filters
                                    </Typography>

                                    <FormControl fullWidth sx={{ mb: 2 }}>
                                        <InputLabel>Type</InputLabel>
                                        <Select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} label="Type">
                                            <MenuItem value="">All</MenuItem>
                                            {entertainment_entity_filter?.EntertainmentEntityTypesV2?.map((type) => (
                                                <MenuItem key={type.value} value={type.value}>
                                                    {type.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Box>

                                <Box sx={{ mt: 'auto', display: 'flex', gap: 1 }}>
                                    <Button
                                        variant="contained"
                                        fullWidth
                                        onClick={handleClearSearch}
                                        sx={{
                                            height: 35,
                                            fontSize: 12,
                                            fontWeight: '400',
                                            backgroundColor: 'red',
                                            borderTopLeftRadius: 0,
                                            borderTopRightRadius: 0,
                                        }}
                                    >
                                        Clear
                                    </Button>
                                    <Button
                                        variant="contained"
                                        fullWidth
                                        onClick={handleSearch}
                                        sx={{
                                            height: 35,
                                            fontSize: 12,
                                            fontWeight: '400',
                                            backgroundColor: 'burlywood',
                                            borderTopLeftRadius: 0,
                                            borderTopRightRadius: 0,
                                        }}
                                    >
                                        Filter
                                    </Button>
                                </Box>
                            </Box>
                        )}
                    </Box>
                </DialogContent>

                <DialogActions dividers={false} sx={{ p: 0 }}>
                    <Button
                        variant="contained"
                        fullWidth
                        onClick={onClose}
                        sx={{
                            height: 50,
                            fontSize: 16,
                            fontWeight: '600',
                            backgroundColor: 'burlywood',
                            borderTopLeftRadius: 0,
                            borderTopRightRadius: 0,
                        }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {selectedEntertainmentEntity && (
                <MediaSelectDialog
                    open={openMediaDialog}
                    onClose={() => {
                        setOpenMediaDialog(false);
                        setSelectedEntertainmentEntity(null);
                    }}
                    onSubmit={(selectedMedia) => {
                        const mediaEntity = {
                            time: selectedItem?.time,
                            entityId: selectedEntertainmentEntity?._id,
                            multimediaId: selectedMedia?._id,
                            details: {
                                multimediaUrl: selectedMedia?.url,
                                entity: selectedEntertainmentEntity,
                                media: selectedMedia
                            }
                        };

                        console.log("selectedMedia : ", selectedMedia)
                        console.log("selectedItem : ", selectedItem)
                        handleChange(mediaEntity);

                        setSelectedEntertainmentEntity(null);
                        setOpenMediaDialog(false);
                    }}
                    selectedEntertainmentEntity={selectedEntertainmentEntity}
                />
            )}
        </>
    );
}

const mapStateToProps = (state) => ({
    ENTITY_GLOBALS_DATA: state?.getEntity?.entity_globals_data,
    ENTITY_GLOBALS_ERROR: state?.getEntity?.entity_globals_error,
    ENTITIES_DATA: state?.getEntity?.entities_data,
    ENTITIES_PAGINATION_DATA: state?.getEntity?.entities_meta,
});

const mapDispatchToProps = {
    getEntities: getEntitiesRequest,
    getEntityGlobals: getEntityGlobalsRequest,
};

export default connect(mapStateToProps, mapDispatchToProps)(RapHousieEntertainmentEntityDialog);
